import React, { useState } from 'react';

const ReviewStep = ({ allData, onChange }) => {
  const { personalDetails, teamMembers, territory, brandingRequests } = allData;
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  
  // Calculate email costs
  const totalEmails = teamMembers?.length || 0;
  const includedEmails = 1;
  const additionalEmails = Math.max(0, totalEmails - includedEmails);
  const additionalCost = additionalEmails * 2500;
  
  // Pass terms acceptance back to parent
  React.useEffect(() => {
    if (onChange) {
      onChange({ acceptedTerms });
    }
  }, [acceptedTerms, onChange]);

  return (
    <div className="onboarding-step review-step">
      <div className="step-header">
        <h2>Review & Submit</h2>
        <p>Please review all the information before submitting your application</p>
      </div>

      <div className="review-sections">
        {/* Personal Details */}
        <div className="review-section">
          <div className="review-section-header">
            <h3>📋 Personal Details</h3>
          </div>
          <div className="review-content">
            <div className="review-item">
              <span className="review-label">Full Name:</span>
              <span className="review-value">{personalDetails?.fullName || '-'}</span>
            </div>
            <div className="review-item">
              <span className="review-label">Phone:</span>
              <span className="review-value">{personalDetails?.phone || '-'}</span>
            </div>
            <div className="review-item">
              <span className="review-label">Emergency Contact:</span>
              <span className="review-value">{personalDetails?.emergencyContact || '-'}</span>
            </div>
            <div className="review-item">
              <span className="review-label">Address:</span>
              <span className="review-value">
                {personalDetails?.address ? 
                  `${personalDetails.address.street}, ${personalDetails.address.city}, ${personalDetails.address.state} - ${personalDetails.address.pincode}`
                  : '-'}
              </span>
            </div>
          </div>
        </div>

        {/* Team Members */}
        <div className="review-section">
          <div className="review-section-header">
            <h3>👥 Team Members ({teamMembers?.length || 0})</h3>
          </div>
          <div className="review-content">
            {teamMembers && teamMembers.length > 0 ? (
              <div className="review-team-list">
                {teamMembers.map((member, index) => (
                  <div key={index} className="review-team-member">
                    <div className="team-member-avatar">
                      {member.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="team-member-info">
                      <strong>{member.name}</strong>
                      <span>📧 {member.email}</span>
                      <span style={{ color: 'var(--auth-gold)', fontWeight: '600' }}>
                        ✉️ {member.preferredEmail}@douglasmccartney.com
                      </span>
                      <span className="member-role">{member.position || member.role}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-data">No team members added</p>
            )}
          </div>
        </div>

        {/* Email Cost Summary */}
        <div className="review-section" style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          border: '2px solid rgba(212, 175, 55, 0.3)',
          borderRadius: '12px'
        }}>
          <div className="review-section-header">
            <h3>💰 Email Account Costs</h3>
          </div>
          <div className="review-content">
            <div className="cost-breakdown" style={{
              display: 'grid',
              gap: '16px',
              padding: '8px 0'
            }}>
              <div className="cost-item" style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '12px 16px',
                background: '#ffffff',
                borderRadius: '8px',
                border: '1px solid #e9ecef'
              }}>
                <span style={{ color: '#6c757d' }}>Total Email Accounts:</span>
                <strong style={{ fontSize: '18px', color: '#2c3e50' }}>{totalEmails}</strong>
              </div>
              <div className="cost-item" style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '12px 16px',
                background: 'linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)',
                borderRadius: '8px',
                border: '2px solid #28a745'
              }}>
                <span style={{ color: '#155724', fontWeight: '500' }}>Included (Free):</span>
                <strong style={{ fontSize: '18px', color: '#155724' }}>1 email</strong>
              </div>
              <div className="cost-item" style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '12px 16px',
                background: 'linear-gradient(135deg, #fef5e7 0%, #fdebd0 100%)',
                borderRadius: '8px',
                border: '2px solid var(--auth-gold)'
              }}>
                <span style={{ color: '#9a7b3b', fontWeight: '500' }}>Additional Emails:</span>
                <strong style={{ fontSize: '18px', color: '#9a7b3b' }}>
                  {additionalEmails} × ₹2,500
                </strong>
              </div>
              <div className="cost-item" style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '16px 20px',
                background: 'linear-gradient(135deg, var(--auth-gold) 0%, #e4c568 100%)',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(212, 175, 55, 0.3)'
              }}>
                <span style={{ color: '#2c3e50', fontWeight: '600', fontSize: '16px' }}>
                  Additional Email Cost:
                </span>
                <strong style={{ fontSize: '24px', color: '#2c3e50' }}>
                  ₹{additionalCost.toLocaleString('en-IN')}
                </strong>
              </div>
            </div>
          </div>
        </div>

        {/* Territory */}
        <div className="review-section">
          <div className="review-section-header">
            <h3>🗺️ Territory & Coverage</h3>
          </div>
          <div className="review-content">
            <div className="review-item">
              <span className="review-label">Primary City:</span>
              <span className="review-value">{territory?.primaryCity || '-'}</span>
            </div>
            <div className="review-item">
              <span className="review-label">Coverage Areas:</span>
              <div className="review-value">
                {territory?.coverageAreas && territory.coverageAreas.length > 0 ? (
                  <div className="review-tags">
                    {territory.coverageAreas.map((area, index) => (
                      <span key={index} className="review-tag">{area}</span>
                    ))}
                  </div>
                ) : '-'}
              </div>
            </div>
            <div className="review-item">
              <span className="review-label">Property Types:</span>
              <div className="review-value">
                {territory?.propertyTypes && territory.propertyTypes.length > 0 ? (
                  <div className="review-tags">
                    {territory.propertyTypes.map((type, index) => (
                      <span key={index} className="review-tag">{type}</span>
                    ))}
                  </div>
                ) : '-'}
              </div>
            </div>
          </div>
        </div>

        {/* Branding Materials */}
        <div className="review-section">
          <div className="review-section-header">
            <h3>🎨 Branding Materials</h3>
          </div>
          <div className="review-content">
            <div className="branding-summary">
              {brandingRequests?.businessCards?.needed && (
                <div className="branding-summary-item">
                  <span className="material-icon">📇</span>
                  <div>
                    <strong>Business Cards</strong>
                    <p>{brandingRequests.businessCards.quantity || 0} units - {brandingRequests.businessCards.urgency}</p>
                  </div>
                </div>
              )}
              
              {brandingRequests?.brochures?.needed && (
                <div className="branding-summary-item">
                  <span className="material-icon">📋</span>
                  <div>
                    <strong>Brochures</strong>
                    <p>{brandingRequests.brochures.quantity || 0} units</p>
                  </div>
                </div>
              )}
              
              {brandingRequests?.digitalAssets && (
                <div className="branding-summary-item">
                  <span className="material-icon">🎨</span>
                  <div><strong>Digital Asset Pack</strong></div>
                </div>
              )}
              
              {brandingRequests?.socialMediaTemplates && (
                <div className="branding-summary-item">
                  <span className="material-icon">📱</span>
                  <div><strong>Social Media Templates</strong></div>
                </div>
              )}
              
              {brandingRequests?.emailSignature && (
                <div className="branding-summary-item">
                  <span className="material-icon">✉️</span>
                  <div><strong>Email Signature</strong></div>
                </div>
              )}
              
              {brandingRequests?.propertyListingTemplates && (
                <div className="branding-summary-item">
                  <span className="material-icon">🏘️</span>
                  <div><strong>Property Listing Templates</strong></div>
                </div>
              )}
              
              {brandingRequests?.letterhead && (
                <div className="branding-summary-item">
                  <span className="material-icon">📄</span>
                  <div><strong>Letterhead</strong></div>
                </div>
              )}

              {brandingRequests?.other && (
                <div className="branding-summary-item full-width">
                  <span className="material-icon">💬</span>
                  <div>
                    <strong>Other Requirements:</strong>
                    <p>{brandingRequests.other}</p>
                  </div>
                </div>
              )}
            </div>

            {brandingRequests?.language && (
              <div className="review-item">
                <span className="review-label">Language Preference:</span>
                <span className="review-value">{brandingRequests.language}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Terms & Conditions */}
      <div className="terms-section" style={{
        background: '#ffffff',
        border: '2px solid #e9ecef',
        borderRadius: '12px',
        padding: '20px',
        marginTop: '24px'
      }}>
        <label style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          cursor: 'pointer',
          fontSize: '14px',
          lineHeight: '1.6'
        }}>
          <input 
            type="checkbox" 
            checked={acceptedTerms}
            onChange={(e) => setAcceptedTerms(e.target.checked)}
            required
            style={{
              marginTop: '4px',
              width: '18px',
              height: '18px',
              cursor: 'pointer',
              accentColor: 'var(--auth-gold)'
            }}
          />
          <span style={{ color: '#2c3e50' }}>
            I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--auth-gold)', fontWeight: '600', textDecoration: 'underline' }}>Terms & Conditions</a> and 
            confirm that all information provided is accurate and complete. I understand that additional email accounts will be charged at ₹2,500 per account.
          </span>
        </label>
      </div>

      <div className="submit-notice" style={{
        marginTop: '24px'
      }}>
        <div className="notice-icon">⏱️</div>
        <div className="notice-content">
          <h4>What happens after submission?</h4>
          <p style={{ marginBottom: '12px', color: '#6c757d' }}>
            We'll set up everything for you within 24 hours:
          </p>
          <ul>
            <li>✅ Your application will be received immediately</li>
            <li>⏳ We'll set up your franchise portal and resources</li>
            <li>📧 Email accounts (@douglasmccartney.com) will be created for you and your team</li>
            <li>🎨 Branding materials will be prepared according to your specifications</li>
            <li>✉️ <strong>You'll receive an email with your credentials and next steps when everything is ready</strong></li>
            <li>🚀 Your franchise dashboard will be activated and accessible</li>
          </ul>
          <div style={{
            marginTop: '16px',
            padding: '12px 16px',
            background: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '8px',
            color: '#856404',
            fontSize: '14px'
          }}>
            <strong>⏰ Expected Setup Time:</strong> Within 24 hours of submission
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewStep;

